import { useCallback, useState } from 'react';
import { useGlobal } from "../context/globalProvider";
import { 
  skipSubscriptionCharge, 
  getActiveChurnLandingPageURL, 
  activateSubscription,
  updateSubscriptions,
  UpdateSubscriptionsRequest,
  createSubscription,
  CreateSubscriptionRequest,
  updateSubscription,
  UpdateSubscriptionRequest,
  CreateOnetimeRequest,
  createOnetime,
  deleteOnetime,
  updateOnetime,
  UpdateOnetimeRequest,
  updateSubscriptionChargeDate,
  updateSubscriptionAddress,
  listPlans,
  listOnetimes,
  createSubscriptions,
  applyDiscountToAddress,
  cancelSubscription as cancelRechargeSubscription
} from "@rechargeapps/storefront-client";
import { toast } from "../hooks/useToast";

export const useSubscription = () => {
  const { session, refreshData, sendEvent, customer } = useGlobal();
  const currentDiscounts = customer?.include?.addresses?.flatMap(address => address.discounts || []);
  const [isLoading, setIsLoading] = useState(false);

  const getProductPlansData = useCallback(async (productId: string) => {
    if (!session) return;
    try {
      setIsLoading(true);
      const { plans } = await listPlans(session, {
        external_product_id: productId
      });
      return plans.sort((a, b) => a.sort_order - b.sort_order);
    } catch (error) {
      console.error("Error fetching product plans:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  const createSubscriptionsData = useCallback(async (data: CreateSubscriptionRequest[], source: string) => {
    if (!session) return;
    setIsLoading(true);
    try {
      const bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);      
      await createSubscriptions(session, data, {commit: true });
      await refreshData();

      for (const subscription of data) {
        sendEvent('subscription_created', { subscriptionData: subscription, source, bundleId });
      }

      sendEvent('bundle_created', { subscriptions: data, source, bundleId });

      toast({
        title: "Success",
        description: "Subscriptions created successfully",
      });
    } catch (error) {
      console.error("Error creating subscriptions:", error);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  /**
   * Update the charge date of a subscription
   * Used to pause subscriptions
   */
  const updateSubscriptionChargeDateData = useCallback(async (subscriptionId: string, skipToDate: string) => {
    if (!session) return;
    setIsLoading(true);
    try {
      
      await updateSubscriptionChargeDate(session, subscriptionId, skipToDate);
      await refreshData();

      toast({
        title: "Success",
        description: "Subscription charge date updated successfully",
      });

      sendEvent('subscription_charge_date_updated', { subscriptionId, skipToDate, original_discount_codes: currentDiscounts });
    } catch (error) {
      toast({
        title: "Error updating subscription charge date",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData]);

  /**
   * Skip a subscription charge
   */
  const skipSubscriptionChargeData = useCallback(async (subscriptionId: string, skipDate: string) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await skipSubscriptionCharge(session, subscriptionId, skipDate);
      await refreshData();
      sendEvent('subscription_charge_skipped', { subscriptionId, skipDate, original_discount_codes: currentDiscounts });
      toast({
        title: "Success",
        description: "Subscription charge skipped successfully",
      });
    } catch (error) {
      toast({
        title: "Error skipping subscription charge",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData]);

  /**
   * Create a new subscription
   */
  const createSubscriptionData = useCallback(async (
    data: CreateSubscriptionRequest, 
    source: string,
    options: {
      addVariant?: string | null;
      productTitle?: string;
      addDiscountCode?: string;
      chargeId?: string;
      chargeCodes?: any[];
    } = {}
  ) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await createSubscription(session, data, {
        commit: true,
      });

      if (options.addDiscountCode && options?.chargeCodes && options?.chargeCodes?.length === 0) {
        try {
          await applyDiscountToAddress(session, data.address_id || "", options.addDiscountCode);
          toast({
            title: "Success",
            description: "Discount applied to address successfully",
          });
        } catch (error) {
          toast({
            title: "Error applying discount to address",
            description: error.message,
            variant: "destructive",
          });
        }
      }

      if (options.addVariant) {
        const { onetimes } = await listOnetimes(session, {
          external_variant_id: options.addVariant,
        });

        if (onetimes.length === 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          await createOnetime(session, {
            address_id: data.address_id,
            external_variant_id: { ecommerce: "47748449337580" },
            next_charge_scheduled_at: data.next_charge_scheduled_at,
            price: '0',
            quantity: 1,
            properties: [
              ...(data.properties || []),
              {
                name: "_gift",
                value: "true",
              }
            ],
          });
        }
      }

      await refreshData();
      sendEvent('subscription_created', { subscriptionData: data, original_discount_codes: currentDiscounts, source });
      toast({
        title: "Success",
        description: "Subscription created successfully",
      });
    } catch (error) {
      toast({
        title: "Error creating subscription",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData]);

  /**
   * Generate a cancelation flow URL
   */
  const cancelSubscriptionData = useCallback(async (subscriptionId: string) => {
    if (!session) return;
    setIsLoading(true);

    let url = null;
    try {
      url = await getActiveChurnLandingPageURL(session, subscriptionId, '/');
      sendEvent('subscription_cancellation_initiated', { subscriptionId, original_discount_codes: currentDiscounts });
    } catch (error) {
      toast({
        title: "Error generating cancelation URL",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      if (url) window.location.href = url;
      if (!url) setIsLoading(false);
    }
  }, [session]);

  /**
   * Cancel a subscription directly (no Recharge churn flow)
   */
  const cancelSubscriptionDirectData = useCallback(async (subscriptionId: string, reason?: string) => {
    if (!session) return false;
    setIsLoading(true);
    try {
      await cancelRechargeSubscription(session, subscriptionId, {
        cancellation_reason: reason || 'No reason provided',
        send_email: true,
      });
      await refreshData();
      sendEvent('subscription_cancelled', { subscriptionId, reason, original_discount_codes: currentDiscounts });
      return true;
    } catch (error) {
      toast({
        title: "Error cancelling subscription",
        description: error.message,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData]);

  /**
   * Activate a subscription
   */
  const activateSubscriptionData = useCallback(async (subscriptionId: string) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await activateSubscription(session, subscriptionId);
      await refreshData();
      sendEvent('subscription_activated', { subscriptionId, original_discount_codes: currentDiscounts });
      toast({
        title: "Success",
        description: "Subscription activated successfully",
      });
    } catch (error) {
      toast({
        title: "Error activating subscription",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData]);

  /**
   * Update multiple subscriptions
   */
  const updateSubscriptionsData = useCallback(async (addressId: string, data: UpdateSubscriptionsRequest[]) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await updateSubscriptions(session, addressId, data, {
        commit: true,
      });
      await refreshData();

      let eventName = 'subscriptions_updated';
      if (data.some((item) => item.external_variant_id)) {
        eventName = 'subscription_product_swapped';
      }

      if (data.some((item) => item.next_charge_scheduled_at)) {
        eventName = 'subscription_rescheduled';
      }

      sendEvent(eventName, { addressId, updates: data, original_discount_codes: currentDiscounts });
      toast({
        title: "Success",
        description: "Subscriptions updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error updating subscriptions",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData]);

  /**
   * Update a single subscription
   */
  const updateSubscriptionData = useCallback(async (subscriptionId: string, data: UpdateSubscriptionRequest) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await updateSubscription(session, subscriptionId, data, {
        commit: true,
      });
      await refreshData();

      let eventName = 'subscription_updated';

      if (data.external_variant_id) {
        eventName = 'subscription_product_swapped';
      }

      if (data.next_charge_scheduled_at) {
        eventName = 'subscription_rescheduled';
      }
        
      sendEvent(eventName, { subscriptionId, updates: data, original_discount_codes: currentDiscounts });
      toast({
        title: "Success",
        description: "Subscription updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error updating subscription",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData]);

  const createOneTimeOrderData = useCallback(async (data: CreateOnetimeRequest, source: string, options: {
    addDiscountCode?: string;
    chargeCodes?: any[];
  } = {}) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await createOnetime(session, data);
      await refreshData();
      sendEvent('onetime_order_created', { orderData: data, original_discount_codes: currentDiscounts, source });
      toast({
        title: "Success",
        description: "One-time order created successfully",
      });
    } catch (error) {
      toast({
        title: "Error creating one-time order",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }

    if (options.addDiscountCode && options?.chargeCodes && options?.chargeCodes?.length === 0) {
      try {
        await applyDiscountToAddress(session, data.address_id || "", options.addDiscountCode);
        toast({
          title: "Success",
          description: "Discount applied to address successfully",
        });
        await refreshData();
      } catch (error) {
        toast({
          title: "Error applying discount to address",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  }, [session, refreshData]);

  const deleteOneTimeOrderData = useCallback(async (oneTimeOrderId: string) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await deleteOnetime(session, oneTimeOrderId);
      await refreshData();
      sendEvent('onetime_order_deleted', { oneTimeOrderId, original_discount_codes: currentDiscounts });
      toast({
        title: "Success",
        description: "One-time order deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error deleting one-time order",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData]);

  const updateOnetimeOrderData = useCallback(async (oneTimeOrderId: string, data: UpdateOnetimeRequest) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await updateOnetime(session, oneTimeOrderId, data);
      await refreshData();
      sendEvent('onetime_order_updated', { oneTimeOrderId, updates: data, original_discount_codes: currentDiscounts });
    } catch (error) {
      toast({
        title: "Error updating one-time order",
        description: error.message,
        variant: "destructive",
      });
    }
  }, [session, refreshData]);


  const updateSubscriptionAddressData = useCallback(async (subscriptionId: string, addressId: string) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await updateSubscriptionAddress(session, subscriptionId, addressId);
      await refreshData();
      sendEvent('subscription_address_updated', { subscriptionId, addressId, original_discount_codes: currentDiscounts });
      toast({
        title: "Success",
        description: "Subscription address updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error updating subscription address",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData]);

  return {
    skipSubscriptionCharge: skipSubscriptionChargeData,
    cancelSubscription: cancelSubscriptionData,
    cancelSubscriptionDirect: cancelSubscriptionDirectData,
    activateSubscription: activateSubscriptionData,
    updateSubscriptions: updateSubscriptionsData,
    createSubscription: createSubscriptionData,
    updateSubscription: updateSubscriptionData,
    createOneTimeOrder: createOneTimeOrderData,
    deleteOneTimeOrder: deleteOneTimeOrderData,
    updateOnetimeOrder: updateOnetimeOrderData,
    updateSubscriptionAddress: updateSubscriptionAddressData,
    updateSubscriptionChargeDate: updateSubscriptionChargeDateData,
    getProductPlans: getProductPlansData,
    createSubscriptions: createSubscriptionsData,
    isLoading,
  };
};
